import { css } from '@emotion/react';
import { IonButton, IonIcon, IonItem, IonLabel, IonList, IonPopover, useIonRouter } from '@ionic/react';
import { signOut } from 'firebase/auth';
import {
  globeOutline,
  hammerOutline,
  helpCircleOutline,
  linkOutline,
  logOutOutline,
  megaphoneOutline,
  menu,
  notificationsOutline,
} from 'ionicons/icons';
import React, { useState } from 'react';
import clarityBlueStroke from '../../assets/img/clarity_blue_stroke.svg';
import { useUser } from '../../contexts/UserContext';
import { auth } from '../../firebaseConfig';
import { createWidgetCreatorUrl } from '../../shared/url-utils';
import * as commonCss from '../../styles/common';
import { NetworkDto } from '../../types';
import { LoginModal } from '../LoginModal';

interface MapMenuButtonProps {
  network: NetworkDto;
  onClickTour: () => void;
}

export const MapMenuButton: React.FC<MapMenuButtonProps> = ({ network, onClickTour }) => {
  const { user } = useUser();
  const router = useIonRouter();
  const [showPopover, setShowPopover] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { name: networkName, networkUrl, isNotificationEnabled, networkId } = network;

  const handleNotificationPreferenceClick = React.useCallback(() => {
    if (!user) {
      setShowPopover(false);
      setShowLoginModal(true);
    } else {
      router.push('/notifications/preferences');
    }
  }, [setShowPopover, setShowLoginModal, user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowPopover(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  React.useEffect(() => {
    if (showLoginModal && user) {
      setShowLoginModal(false);
      router.push('/notifications/preferences');
    }
  }, [user]);

  const menuCss = {
    buttonContainer: css({
      position: 'absolute',
      bottom: 18, // align with legend
      right: 10,
    }),
    button: css({
      '--border-radius': '4px',
    }),
    popover: css({
      '--width': '17rem',
    }),
    clarityIcon: css({
      filter: 'grayscale(100%)',
    }),
  };

  return (
    <>
      <IonButton
        css={[menuCss.buttonContainer, menuCss.button]}
        id="map-menu-button"
        size="default"
        color="primary"
        onClick={() => setShowPopover(true)}
      >
        <IonIcon icon={menu} slot="icon-only" />
      </IonButton>
      <IonPopover
        //trigger="map-menu-button"
        reference="trigger"
        side="bottom"
        size="auto"
        alignment="start"
        css={menuCss.popover}
        isOpen={showPopover}
        onDidDismiss={() => setShowPopover(false)}
      >
        <IonList css={commonCss.noPadding}>
          <IonItem button href={networkUrl} target="_blank" rel="noopener noreferrer">
            <IonIcon slot="start" icon={linkOutline} />
            <IonLabel>About {networkName}</IonLabel>
          </IonItem>
          <IonItem button href="https://clarity.io" target="_blank" rel="noopener noreferrer">
            <IonIcon slot="start" src={clarityBlueStroke} css={menuCss.clarityIcon} />
            <IonLabel>About Clarity</IonLabel>
          </IonItem>
          <IonItem
            button
            onClick={() => {
              setShowPopover(false);
              onClickTour();
            }}
          >
            <IonIcon slot="start" src={helpCircleOutline} />
            <IonLabel>Help</IonLabel>
          </IonItem>
          <IonItem button href={createWidgetCreatorUrl(networkId)} target="_blank" rel="noopener noreferrer">
            <IonIcon slot="start" src={hammerOutline} />
            <IonLabel>Widget Builder</IonLabel>
          </IonItem>
          <IonItem
            button
            href="https://survey.hsforms.com/1HDrUBRteT72fVUKg6X4XHg4gx9p"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IonIcon slot="start" src={megaphoneOutline} />
            <IonLabel>Give Feedback</IonLabel>
          </IonItem>
          {networkId !== 'world' && (
            <IonItem button href="/world">
              <IonIcon slot="start" src={globeOutline} />
              <IonLabel>Explore Global Network</IonLabel>
            </IonItem>
          )}
          {isNotificationEnabled && (
            <IonItem button onClick={handleNotificationPreferenceClick}>
              <IonIcon slot="start" icon={notificationsOutline} />
              <IonLabel>Notification Preferences</IonLabel>
            </IonItem>
          )}
          {user && (
            <IonItem button onClick={handleLogout}>
              <IonIcon slot="start" icon={logOutOutline} />
              <IonLabel>Logout</IonLabel>
            </IonItem>
          )}
        </IonList>
      </IonPopover>
      <LoginModal
        isOpen={showLoginModal}
        setIsOpen={setShowLoginModal}
        title="Notification Preferences"
        promptText="Please, sign in to manage your notification preferences."
      />
    </>
  );
};
